import React, { useEffect, useMemo, useState } from 'react';
import { Search, Filter, CheckCircle, XCircle, Settings, Users, Clock, FileText, UserPlus } from 'lucide-react';
import './AdminVendors.css';
import CreateVendorModal from './CreateVendorModal';
import { API_BASE_URL } from '../../api';

const AdminVendors = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [activeTab, setActiveTab] = useState('ALL');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [reason, setReason] = useState('');
  const [actionVendorId, setActionVendorId] = useState(null);
  const [actionType, setActionType] = useState('');
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkMode, setBulkMode] = useState('approval');
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [detailsVendorId, setDetailsVendorId] = useState(null);
  const [editingVendor, setEditingVendor] = useState(null);
  const [approvalFilter, setApprovalFilter] = useState('');
  const [activityFilter, setActivityFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [confirmingActivity, setConfirmingActivity] = useState({ id: null, next: '', prev: '' });
  const [confirmingApproval, setConfirmingApproval] = useState({ id: null, next: '' });
  const [toast, setToast] = useState({ text: '', type: 'success', visible: false });
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);
  const [showCreateVendorModal, setShowCreateVendorModal] = useState(false);
  const [activitySubmitting, setActivitySubmitting] = useState(false);

  const statusTabs = useMemo(() => ([
    { key: 'ALL', label: 'All' },
    { key: 'SUBMITTED', label: 'Pending' },
    { key: 'APPROVED', label: 'Approved' },
    { key: 'REJECTED', label: 'Rejected' },
    { key: 'SUSPENDED', label: 'Suspended' },
  ]), []);

  const statusOptions = useMemo(() => ([
    { value: 'DRAFT', label: 'Draft', color: '#6b7280' },
    { value: 'SUBMITTED', label: 'Pending', color: '#f59e0b' },
    { value: 'IN_REVIEW', label: 'Under Review', color: '#3b82f6' },
    { value: 'APPROVED', label: 'Approved', color: '#10b981' },
    { value: 'ACTIVE', label: 'Active', color: '#059669' },
    { value: 'REJECTED', label: 'Rejected', color: '#dc2626' },
    { value: 'SUSPENDED', label: 'Suspended', color: '#ef4444' },
  ]), []);

  // Map frontend status to backend status
  const mapStatusToBackend = (frontendStatus) => {
    const statusMap = {
      'pending': 'SUBMITTED',
      'under_review': 'IN_REVIEW',
      'approved': 'APPROVED',
      'active': 'ACTIVE',
      'rejected': 'REJECTED',
      'suspended': 'SUSPENDED',
      'draft': 'DRAFT'
    };
    return statusMap[frontendStatus?.toLowerCase()] || frontendStatus;
  };

  // Map backend status (any case) to frontend status value used by <select>
  const mapStatusFromBackend = (backendStatus) => {
    const key = String(backendStatus || '').toUpperCase();
    const statusMap = {
      'SUBMITTED': 'pending',
      'IN_REVIEW': 'under_review',
      'UNDER_REVIEW': 'under_review',
      'APPROVED': 'approved',
      'ACTIVE': 'active',
      'REJECTED': 'rejected',
      'SUSPENDED': 'suspended',
      'DRAFT': 'draft'
    };
    return statusMap[key] || String(backendStatus || '').toLowerCase();
  };

  const getStatusBadge = (status) => {
    const key = String(status || '').toUpperCase();
    const statusClasses = {
      'DRAFT': 'status-draft',
      'SUBMITTED': 'status-pending',
      'IN_REVIEW': 'status-review',
      'UNDER_REVIEW': 'status-review',
      'APPROVED': 'status-approved',
      'ACTIVE': 'status-active',
      'REJECTED': 'status-blocked',
      'SUSPENDED': 'status-suspended'
    };
    return statusClasses[key] || 'status-default';
  };

  const getStatusColor = (status) => {
    const mappedStatus = mapStatusFromBackend(status);
    const option = statusOptions.find(opt => mapStatusFromBackend(opt.value) === mappedStatus);
    return option ? option.color : '#6b7280';
  };

  const fetchVendors = async () => {
    try {
      setLoading(true);
      setError('');
      const qs = new URLSearchParams();
      if (activeTab && activeTab !== 'ALL') qs.set('status', activeTab.toLowerCase());
      if (cityFilter) qs.set('city', cityFilter);
      if (searchQuery) qs.set('search', searchQuery);
      if (flaggedOnly) qs.set('flagged', 'true');
      if (approvalFilter) qs.set('approval_status', approvalFilter);
      if (activityFilter) qs.set('activity_status', activityFilter);
      if (dateFrom) qs.set('date_from', dateFrom);
      if (dateTo) qs.set('date_to', dateTo);
      qs.set('page', String(page));
      qs.set('limit', String(limit));
      const res = await fetch(`${API_BASE_URL}/api/admin/vendors?${qs.toString()}`, {
        headers: { 'x-admin-key': (window.localStorage.getItem('ADMIN_API_KEY') || 'dev-admin-key') },
        credentials: 'include'
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Failed to load vendors');
      }
      const data = await res.json();
      setVendors(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
    // eslint-disable-next-line
  }, [activeTab, cityFilter, searchQuery, flaggedOnly, approvalFilter, activityFilter, dateFrom, dateTo, page, limit]);

  const openAction = (vendorId, type) => {
    setActionVendorId(vendorId);
    setActionType(type);
    setReason('');
  };

  const showToast = (text, type = 'success', timeoutMs = 2500) => {
    setToast({ text, type, visible: true });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(prev => ({ ...prev, visible: false })), timeoutMs);
  };

  const submitAction = async () => {
    if (!actionVendorId || !actionType) return;
    let endpoint = 'approve';
    if (actionType === 'reject') endpoint = 'reject';
    else if (actionType === 'suspend') endpoint = 'suspend';
    else if (actionType === 'activate') endpoint = 'activate';
    try {
      setIsSubmittingAction(true);
      const res = await fetch(`${API_BASE_URL}/api/admin/vendors/${actionVendorId}/${endpoint}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': (window.localStorage.getItem('ADMIN_API_KEY') || 'dev-admin-key')
        },
        credentials: 'include',
        body: JSON.stringify({ reason })
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Action failed');
      }
      setActionVendorId(null);
      setActionType('');
      setReason('');
      fetchVendors();
      showToast('Vendor updated');
    } catch (e) {
      showToast(e.message || 'Action failed', 'error');
    }
    finally {
      setIsSubmittingAction(false);
    }
  };

  const updateVendorStatus = async (vendorId, newStatus, notes = '') => {
    try {
      // Map frontend status to backend status
      const backendStatus = mapStatusToBackend(newStatus);
      
      const res = await fetch(`${API_BASE_URL}/api/admin/vendors/${vendorId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': (window.localStorage.getItem('ADMIN_API_KEY') || 'dev-admin-key')
        },
        body: JSON.stringify({ status: backendStatus, notes })
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Status update failed');
      }
      fetchVendors();
      showToast('Status updated');
    } catch (e) {
      showToast(e.message || 'Status update failed', 'error');
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedVendors.length === 0) return;
    
    try {
      setIsBulkSubmitting(true);
      const res = await fetch(`${API_BASE_URL}/api/admin/vendors/bulk-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': (window.localStorage.getItem('ADMIN_API_KEY') || 'dev-admin-key')
        },
        body: JSON.stringify({ 
          ids: selectedVendors, 
          status: bulkAction
        })
      });
      
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Bulk action failed');
      }
      
      await res.json().catch(() => ({}));
      showToast('Bulk action completed');
      
      setSelectedVendors([]);
      setBulkAction('');
      setShowBulkActions(false);
      fetchVendors();
    } catch (e) {
      showToast(e.message || 'Bulk action failed', 'error');
    } finally {
      setIsBulkSubmitting(false);
    }
  };

  const deleteVendor = async (vendorId) => {
    if (!window.confirm('Permanently delete this vendor? This cannot be undone.')) return;
    try {
      setDeletingId(vendorId);
      const res = await fetch(`${API_BASE_URL}/api/admin/vendors/${vendorId}`, {
        method: 'DELETE',
        headers: {
          'x-admin-key': (window.localStorage.getItem('ADMIN_API_KEY') || 'dev-admin-key')
        }
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Delete failed');
      }
      fetchVendors();
      showToast('Vendor deleted');
    } catch (e) {
      showToast(e.message || 'Delete failed', 'error');
    } finally {
      setDeletingId(prev => (prev === vendorId ? null : prev));
    }
  };

  const toggleVendorSelection = (vendorId) => {
    setSelectedVendors(prev => 
      prev.includes(vendorId) 
        ? prev.filter(id => id !== vendorId)
        : [...prev, vendorId]
    );
  };

  const selectAllVendors = () => {
    setSelectedVendors(vendors.map(v => v.id));
  };

  const clearSelection = () => {
    setSelectedVendors([]);
  };


  const openDetails = (vendorId) => {
    setDetailsVendorId(vendorId);
    setShowDetails(true);
  };

  const openEdit = (vendor) => {
    setEditingVendor(vendor);
  };

  const toggleActivity = (vendor) => {
    const current = String(vendor.activity_status || '').toUpperCase();
    const next = current === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    // Optimistically update UI
    setVendors(prev => prev.map(v => v.id === vendor.id ? { ...v, activity_status: next } : v));
    setConfirmingActivity({ id: vendor.id, next, prev: current });
  };

  const confirmToggleActivity = async () => {
    if (!confirmingActivity.id) return;
    try {
      setActivitySubmitting(true);
      const res = await fetch(`${API_BASE_URL}/api/admin/vendors/${confirmingActivity.id}/activity`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-admin-key': (window.localStorage.getItem('ADMIN_API_KEY') || 'dev-admin-key') },
        body: JSON.stringify({ status: confirmingActivity.next })
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Failed to update activity status');
      }
      setConfirmingActivity({ id: null, next: '', prev: '' });
      // Optional refresh to sync any backend-derived flags/timestamps
      fetchVendors();
      showToast('Activity status updated');
    } catch (e) {
      // Revert optimistic update
      setVendors(prev => prev.map(v => v.id === confirmingActivity.id ? { ...v, activity_status: confirmingActivity.prev } : v));
      setConfirmingActivity({ id: null, next: '', prev: '' });
      showToast(e.message || 'Failed to update activity status', 'error');
    } finally {
      setActivitySubmitting(false);
    }
  };

  const cancelToggleActivity = () => {
    if (confirmingActivity.id) {
      // Revert optimistic update
      setVendors(prev => prev.map(v => v.id === confirmingActivity.id ? { ...v, activity_status: confirmingActivity.prev } : v));
    }
    setConfirmingActivity({ id: null, next: '', prev: '' });
  };

  const openApprovalChange = (vendor, next) => {
    setConfirmingApproval({ id: vendor.id, next });
  };

  const confirmApprovalChange = async () => {
    if (!confirmingApproval.id) return;
    try {
      setApprovalSubmitting(true);
      let status = String(confirmingApproval.next || '').toUpperCase();
      if (status === 'PENDING') status = 'SUBMITTED';
      if (status === 'IN_REVIEW') status = 'UNDER_REVIEW';
      const res = await fetch(`${API_BASE_URL}/api/admin/vendors/${confirmingApproval.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-admin-key': (window.localStorage.getItem('ADMIN_API_KEY') || 'dev-admin-key') },
        body: JSON.stringify({ status })
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Failed to update approval status');
      }
      setConfirmingApproval({ id: null, next: '' });
      fetchVendors();
      showToast('Approval status updated');
    } catch (e) {
      showToast(e.message || 'Failed to update approval status', 'error');
    } finally {
      setApprovalSubmitting(false);
    }
  };

  const cancelApprovalChange = () => setConfirmingApproval({ id: null, next: '' });

  const handleCreateVendor = () => {
    setShowCreateVendorModal(true);
  };

  const handleVendorCreated = (vendorData) => {
    // Refresh the vendors list by fetching again
    fetchVendors();
    setShowCreateVendorModal(false);
    showToast('Vendor account created successfully!', 'success');
  };

  const handleCloseCreateModal = () => {
    setShowCreateVendorModal(false);
  };

  return (
    <div className="admin-vendors">
      <div className="admin-vendors-header">
        <div className="admin-vendors-header-content">
          <h1>Vendor Management</h1>
          <p>Manage and monitor all vendors on the platform</p>
        </div>
        <button className="btn btn-primary" onClick={handleCreateVendor}>
          <UserPlus size={20} />
          Create Vendor Account
        </button>
      </div>

      <div className="admin-tabs">
        {statusTabs.map(t => (
          <button
            key={t.key}
            className={`admin-tab ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => { setActiveTab(t.key); setPage(1); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="admin-vendors-filters">
        <div className="admin-filters-left">
          <div className="admin-search-container">
            <Search size={20} className="admin-search-icon" />
            <input
              type="text"
              placeholder="Search vendors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="admin-search-input"
            />
            <div className="admin-results-count" style={{ marginLeft: 12 }}>
              {vendors.length} of {total} vendors
            </div>
          </div>
          
          <div className="admin-filter-group">
            <Filter size={20} />
            <input
              type="text"
              placeholder="City"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="admin-search-input"
              style={{ width: 180 }}
            />
          </div>
          <div className="admin-filter-group">
            <select className="admin-filter-select" value={approvalFilter} onChange={(e) => { setApprovalFilter(e.target.value); setPage(1); }}>
              <option value="">All Approval Status</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="DRAFT">Draft</option>
            </select>
          </div>
          <div className="admin-filter-group">
            <select className="admin-filter-select" value={activityFilter} onChange={(e) => { setActivityFilter(e.target.value); setPage(1); }}>
              <option value="">All Activity</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <input type="checkbox" checked={flaggedOnly} onChange={(e) => { setFlaggedOnly(e.target.checked); setPage(1); }} />
            Flagged only
          </label>
        </div>

        <div className="admin-filters-right">
          <div className="admin-filter-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="date"
              className="admin-search-input"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              aria-label="From date"
              style={{ width: 160 }}
            />
            <span style={{ color: '#666', fontSize: 12 }}>to</span>
            <input
              type="date"
              className="admin-search-input"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              aria-label="To date"
              style={{ width: 160 }}
            />
          </div>
          {selectedVendors.length > 0 && (
            <div className="admin-bulk-actions">
              <span className="admin-selected-count">
                {selectedVendors.length} selected
              </span>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowBulkActions(!showBulkActions)}
              >
                <Settings size={16} />
                Bulk Actions
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</div>
      )}

      {showBulkActions && selectedVendors.length > 0 && (
        <div className="admin-bulk-actions-panel">
          <div className="admin-bulk-actions-content">
            <h4>Bulk Actions ({selectedVendors.length} vendors selected)</h4>
            <div className="admin-bulk-controls">
              <select
                className="admin-filter-select"
                value={bulkMode}
                onChange={(e) => { setBulkMode(e.target.value); setBulkAction(''); }}
              >
                <option value="approval">Approval Status</option>
                <option value="activity">Activity Status</option>
              </select>

              {bulkMode === 'approval' ? (
                <select 
                  value={bulkAction} 
                  onChange={(e) => setBulkAction(e.target.value)}
                  className="admin-filter-select"
                >
                  <option value="">Select Approval</option>
                  <option value="SUBMITTED">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="DRAFT">Draft</option>
                </select>
              ) : (
                <select 
                  value={bulkAction} 
                  onChange={(e) => setBulkAction(e.target.value)}
                  className="admin-filter-select"
                >
                  <option value="">Select Activity</option>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              )}
              <button 
                className="btn btn-primary"
                onClick={handleBulkAction}
                disabled={!bulkAction || isBulkSubmitting}
              >
                {isBulkSubmitting ? 'Applying…' : `Apply ${bulkMode} to ${selectedVendors.length} vendors`}
              </button>
              <button 
                className="btn btn-secondary"
                onClick={clearSelection}
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-vendors-table-container admin-vendors-table-desktop">
        <table className="admin-vendors-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Vendor ID</th>
              <th>Vendor</th>
              <th>Email</th>
              <th>City</th>
              <th>Phone</th>
              <th>Address</th>
              <th>Approval Status</th>
              <th>Registered</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && vendors.map((vendor) => (
              <tr key={vendor.id}>
                <td>#{vendor.id}</td>
                <td>
                  <div className="admin-vendor-id">{vendor.vendor_id || 'N/A'}</div>
                </td>
                <td>
                  <div className="admin-vendor-name">{vendor.shop_name}</div>
                </td>
                <td>{vendor.email}</td>
                <td>{vendor.city || '-'}</td>
                <td>{vendor.phone}</td>
                <td>{vendor.shop_address}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={`admin-status-badge ${String(vendor.approval_status || '').toUpperCase() === 'APPROVED' ? 'status-approved' : String(vendor.approval_status || '').toUpperCase() === 'REJECTED' ? 'status-blocked' : 'status-pending'}`}>
                      {String(vendor.approval_status || '').toUpperCase()}
                    </span>
                  </div>
                </td>
                
                <td>{vendor.created_at ? new Date(vendor.created_at).toLocaleDateString() : '-'}</td>
                <td>
                  <div className="admin-vendor-actions">
                    {/* Approval status quick-set icons */}
                    <button
                      className="admin-action-btn approve"
                      onClick={() => openApprovalChange(vendor, 'APPROVED')}
                      title="Set Approved"
                    >
                      <CheckCircle size={16} />
                    </button>
                    <button
                      className="admin-action-btn block"
                      onClick={() => openApprovalChange(vendor, 'REJECTED')}
                      title="Set Rejected"
                    >
                      <XCircle size={16} />
                    </button>
                    <button
                      className="admin-action-btn view"
                      onClick={() => openApprovalChange(vendor, 'PENDING')}
                      title="Set Pending"
                    >
                      <Clock size={16} />
                    </button>
                    <button
                      className="admin-action-btn edit"
                      onClick={() => openApprovalChange(vendor, 'DRAFT')}
                      title="Set Draft"
                    >
                      <FileText size={16} />
                    </button>
                    <button className="admin-action-btn view" onClick={() => openDetails(vendor.id)} title="View Vendor">
                      <Users size={16} />
                    </button>
                    <button className="admin-action-btn edit" onClick={() => openEdit(vendor)} title="Edit Vendor">
                      <Settings size={16} />
                    </button>
                    <button className="admin-action-btn block" onClick={() => deleteVendor(vendor.id)} title="Delete Vendor" disabled={deletingId === vendor.id}>
                      <XCircle size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {loading && (
              <tr>
                <td colSpan={9} style={{ padding: 24, textAlign: 'center' }}>Loading...</td>
              </tr>
            )}
            {!loading && vendors.length === 0 && (
              <tr>
                <td colSpan={9} style={{ padding: 24, textAlign: 'center' }}>No vendors found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="admin-vendors-mobile-list">
        {!loading && vendors.map((vendor) => (
          <div key={`card-${vendor.id}`} className="admin-vendor-card">
            <div className="admin-vendor-card-header">
              <div>
                <p className="admin-vendor-card-id">#{vendor.id}</p>
                <h3>{vendor.shop_name}</h3>
                <span>{vendor.vendor_id || 'N/A'}</span>
              </div>
              <span className={`admin-status-badge ${String(vendor.approval_status || '').toUpperCase() === 'APPROVED' ? 'status-approved' : String(vendor.approval_status || '').toUpperCase() === 'REJECTED' ? 'status-blocked' : 'status-pending'}`}>
                {String(vendor.approval_status || '').toUpperCase()}
              </span>
            </div>
            <div className="admin-vendor-card-meta">
              <div>
                <label>Email</label>
                <p>{vendor.email}</p>
              </div>
              <div>
                <label>Phone</label>
                <p>{vendor.phone}</p>
              </div>
              <div>
                <label>City</label>
                <p>{vendor.city || '-'}</p>
              </div>
              <div>
                <label>Registered</label>
                <p>{vendor.created_at ? new Date(vendor.created_at).toLocaleDateString() : '-'}</p>
              </div>
            </div>
            {vendor.shop_address && (
              <div className="admin-vendor-card-address">
                <label>Address</label>
                <p>{vendor.shop_address}</p>
              </div>
            )}
            <div className="admin-vendor-card-actions">
              <button className="admin-action-btn approve" onClick={() => openApprovalChange(vendor, 'APPROVED')}>
                <CheckCircle size={16} />
              </button>
              <button className="admin-action-btn block" onClick={() => openApprovalChange(vendor, 'REJECTED')}>
                <XCircle size={16} />
              </button>
              <button className="admin-action-btn view" onClick={() => openApprovalChange(vendor, 'PENDING')}>
                <Clock size={16} />
              </button>
              <button className="admin-action-btn edit" onClick={() => openApprovalChange(vendor, 'DRAFT')}>
                <FileText size={16} />
              </button>
              <button className="admin-action-btn view" onClick={() => openDetails(vendor.id)}>
                <Users size={16} />
              </button>
              <button className="admin-action-btn edit" onClick={() => openEdit(vendor)}>
                <Settings size={16} />
              </button>
              <button className="admin-action-btn block" onClick={() => deleteVendor(vendor.id)} disabled={deletingId === vendor.id}>
                <XCircle size={16} />
              </button>
            </div>
          </div>
        ))}
        {loading && (
          <div className="admin-mobile-placeholder">Loading vendors…</div>
        )}
        {!loading && vendors.length === 0 && (
          <div className="admin-mobile-placeholder">No vendors found</div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span>Rows:</span>
          <select value={limit} onChange={(e) => { setLimit(parseInt(e.target.value)); setPage(1); }}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
        <div className="pagination">
          <button className="btn btn-secondary" disabled={page === 1 || loading} onClick={() => setPage(page - 1)}>Previous</button>
          <span className="page-info">Page {page} of {Math.max(1, Math.ceil((total || 0) / (limit || 1)))}</span>
          <button className="btn btn-secondary" disabled={loading || page >= Math.ceil((total || 0) / (limit || 1))} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      </div>

      {actionVendorId && (
        <div className="admin-modal-backdrop">
          <div className="admin-modal">
            <h3>{actionType === 'approve' ? 'Approve Vendor' : actionType === 'reject' ? 'Reject Vendor' : actionType === 'suspend' ? 'Suspend Vendor' : 'Activate Vendor'}</h3>
            <p>Provide a note{actionType === 'reject' ? ' (reason required)' : ''}:</p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="admin-textarea"
            />
            <div className="admin-modal-actions">
              <button className="btn btn-secondary" onClick={() => { setActionVendorId(null); setActionType(''); }}>Cancel</button>
              <button className="btn btn-primary" onClick={submitAction}>{actionType === 'approve' ? 'Approve' : actionType === 'reject' ? 'Reject' : actionType === 'suspend' ? 'Suspend' : 'Activate'}</button>
            </div>
          </div>
        </div>
      )}

      {confirmingActivity.id && (
        <div className="admin-modal-backdrop">
          <div className="admin-modal">
            <h3>Confirm Activity Change</h3>
            <p>Are you sure you want to set activity to {confirmingActivity.next}?</p>
            <div className="admin-modal-actions">
              <button className="btn btn-secondary" onClick={cancelToggleActivity} disabled={activitySubmitting}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmToggleActivity} disabled={activitySubmitting}>{activitySubmitting ? 'Saving…' : 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}

      {confirmingApproval.id && (
        <div className="admin-modal-backdrop">
          <div className="admin-modal">
            <h3>Confirm Approval Status</h3>
            <p>Change approval status to {String(confirmingApproval.next || '').toUpperCase()}?</p>
            <div className="admin-modal-actions">
              <button className="btn btn-secondary" onClick={cancelApprovalChange} disabled={approvalSubmitting}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmApprovalChange} disabled={approvalSubmitting}>{approvalSubmitting ? 'Saving…' : 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}


      {showDetails && (
        <VendorDetailsModal vendorId={detailsVendorId} onClose={() => setShowDetails(false)} />
      )}

      {editingVendor && (
        <EditVendorModal vendor={editingVendor} onClose={() => setEditingVendor(null)} onSaved={fetchVendors} />
      )}

      <CreateVendorModal
        isOpen={showCreateVendorModal}
        onClose={handleCloseCreateModal}
        onVendorCreated={handleVendorCreated}
      />

      {toast.visible && (
        <div
          className={`admin-toast ${toast.type === 'error' ? 'admin-toast-error' : 'admin-toast-success'}`}
          style={{ position: 'fixed', right: 16, bottom: 16, padding: '8px 12px', borderRadius: 6, color: '#fff', background: toast.type === 'error' ? '#dc2626' : '#10b981', boxShadow: '0 6px 16px rgba(0,0,0,0.2)' }}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
};

// Details Modal
const VendorDetailsModal = ({ vendorId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fetch(`${API_BASE_URL}/api/admin/vendors/${vendorId}`, { headers: { 'x-admin-key': (window.localStorage.getItem('ADMIN_API_KEY') || 'dev-admin-key') } });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.error || 'Failed to load vendor');
        }
        const json = await res.json();
        setData(json);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    if (vendorId) load();
  }, [vendorId]);

  if (!vendorId) return null;

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Vendor Details</h3>
        {loading && <div>Loading...</div>}
        {error && <div style={{ color: '#b91c1c' }}>{error}</div>}
        {data && (
          <div className="admin-details-content">
            <div style={{ marginBottom: 12 }}>
              <strong>{data.vendor.shop_name}</strong><br />
              {data.vendor.owner_name} • {data.vendor.email} • {data.vendor.phone}
              <div style={{ fontSize: 12, color: '#666' }}>{data.vendor.shop_address}</div>
              {data.vendor.city && <div style={{ fontSize: 12, color: '#666' }}>City: {data.vendor.city}</div>}
              <div style={{ fontSize: 12, color: '#666' }}>Status: {data.vendor.status}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <h4>Stats</h4>
                <div>Products: {data.stats?.productsCount || 0}</div>
                <div>Inventory SKUs: {data.stats?.inventoryStats?.totalSkus || 0}</div>
                <div>Total Stock: {data.stats?.inventoryStats?.totalStock || 0}</div>
              </div>
              <div>
                <h4>Recent Orders</h4>
                {Array.isArray(data.recentOrders) && data.recentOrders.length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {data.recentOrders.map(o => (
                      <li key={o.id}>#{o.id} • {o.status} • ₹{o.total_amount} • {new Date(o.created_at).toLocaleString()}</li>
                    ))}
                  </ul>
                ) : (
                  <div>No orders</div>
                )}
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <h4>Documents</h4>
              {Array.isArray(data.documents) && data.documents.length > 0 ? (
                <table className="admin-vendors-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Size</th>
                      <th>Uploaded</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.documents.map(d => (
                      <tr key={d.id}>
                        <td>{d.document_type}</td>
                        <td>{d.original_name}</td>
                        <td>{Math.round((d.file_size || 0) / 1024)} KB</td>
                        <td>{new Date(d.uploaded_at).toLocaleString()}</td>
                        <td>
                          <a className="btn btn-secondary" href={`/${String(d.file_path || '').replace('\\','/')}`} target="_blank" rel="noreferrer">View</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div>No documents</div>
              )}
            </div>
          </div>
        )}
        <div className="admin-modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// Edit Modal
const EditVendorModal = ({ vendor, onClose, onSaved }) => {
  const [form, setForm] = useState({
    shopName: vendor?.shop_name || '',
    ownerName: vendor?.owner_name || '',
    email: vendor?.email || '',
    phone: vendor?.phone || '',
    shopAddress: vendor?.shop_address || '',
    city: vendor?.city || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const save = async () => {
    try {
      setSaving(true);
      setError('');
      const res = await fetch(`${API_BASE_URL}/api/admin/vendors/${vendor.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-admin-key': (window.localStorage.getItem('ADMIN_API_KEY') || 'dev-admin-key') },
        body: JSON.stringify(form)
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Save failed');
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Edit Vendor</h3>
        {error && <div style={{ color: '#b91c1c' }}>{error}</div>}
        <div className="modal-form">
          <div className="form-grid">
            <div className="form-group">
              <label>Shop Name</label>
              <input name="shopName" value={form.shopName} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Owner Name</label>
              <input name="ownerName" value={form.ownerName} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input name="email" value={form.email} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input name="phone" value={form.phone} onChange={handleChange} />
            </div>
            <div className="form-group full-width">
              <label>Address</label>
              <input name="shopAddress" value={form.shopAddress} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>City</label>
              <input name="city" value={form.city} onChange={handleChange} />
            </div>
          </div>
        </div>
        <div className="admin-modal-actions">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

export default AdminVendors;
